// LIVE. This page is published from `main` by the Lovable front-end build and every RPC it
// calls exists in production. The previous line 1 read "DRAFT — REVIEW ARTIFACT — NOT
// DEPLOYED", which was false in the same way `services/reporting-compute/app.py`'s header was
// before 2026-08-17: it reads as "nothing here is real yet", and it is why the missing half of
// this flow looked like a blocked dependency rather than an unbuilt feature. Corrected
// 2026-08-21, measured against `pg_proc` on project ewyfhrjexfrmcjhqzsmm.
//
// The human curation surface for the `market_context` slot (Track 1d). It is the ONLY door to
// all FOUR steps of the curation flow, in the order they must run:
//
//   1. yardeni_register_chartbook_edition — attest the licensed edition (its frozen content hash).
//   2. yardeni_import_top_findings        — import the offline synthesis + a draft slot.
//   3. yardeni_verify_chart_read          — clear needs_human_verify on each candidate read.
//   4. yardeni_curate_and_publish         — publish the slot for the period.
//
// ⚠️ STEPS 1 AND 2 HAD NO UI ANYWHERE IN THE REPO until 2026-08-21, which made steps 3 and 4
// unreachable: this page's own empty state told the reader to "import the offline synthesis
// output (yardeni_import_top_findings) before a slot can be published here", and there was
// nowhere to do that. Measured at the time: `yardeni_synthesis_runs` and
// `yardeni_chartbook_editions` were both EMPTY — 0 rows, ever, for any period — so the market
// note had never been authorable on any cycle, and `narrative.investment_team_market_note` sat
// gate-blocking at `exhausted:3` on cycle ec8e622a as a result.
//
// Steps 3 and 4 are the two independent signs, both fail-closed server-side:
//
//   3. yardeni_verify_chart_read  — clears needs_human_verify on a yardeni_chart_reads row
//      (a licensed Yardeni/FactSet forward-earnings chart candidate). This is what lets a
//      figure later carry engine='vendor_chart_verified' in a published slot — a vision-OCR
//      read can NEVER wear that label until a named human has been through here.
//   4. yardeni_curate_and_publish — publishes an imported/curated yardeni_synthesis_runs row
//      as the period's market_context slot. The RPC re-runs the HARD lineage assertion
//      (every realised figure must trace to a verified read / resolvable free-source row /
//      named manual lock / compute inputs) BEFORE flipping to published and REFUSES on any
//      doubt — the button here is UX only, exactly like GateTab's sign-off.
//
// ALL FOUR RPCs are Investment/Operations ONLY (no service_role, no admin escape) — admins are
// shown a read-only banner, mirroring GateTab. Verified against live `prosrc`: each opens with
// `IF auth.uid() IS NULL THEN RAISE ... 42501` and then
// `IF NOT (is_investment_team() OR is_operations_team()) THEN RAISE ... 42501`. That second
// shape is only safe because migration 20260820173941 made both predicates `SELECT EXISTS (…)`,
// which returns false rather than NULL for a caller with no `profiles` row; before it, `IF NOT
// NULL THEN` did not take the branch and every one of these guards was a no-op. Do not write a
// NEW guard in that shape — use `IF <guard>() IS NOT TRUE THEN RAISE`.
//
// There is no dedicated list RPC for these tables yet, so this page reads yardeni_chart_reads /
// yardeni_synthesis_runs / yardeni_chartbook_editions directly (all carry a wide authenticated
// SELECT policy per 0003d_market_context.sql §5/§6); all WRITES still go exclusively through
// the four SECURITY DEFINER RPCs above.
//
// WHY THE IMPORT FORM TALKS TO AN EDGE FUNCTION. `_top_findings` and the draft `market_context`
// are structured JSON a human should not be hand-authoring into a textarea. `reporting-jpm-
// market-review` (deployed v1, LLM-free, writes NOTHING) exists for exactly this hop: it parses
// the J.P. Morgan AM Monthly Market Review into a PROPOSED payload and returns it. Facts —
// index returns, what led and lagged — are not copyrightable and CBAM may state them with the
// source named in provenance; JPM's PROSE is never republished, and is retained only in the
// private `_jpm_sense_check` field that switches on the 6-word n-gram `jpm_overlap` guard in
// research-report-narrative. The human reviews every figure and submits; the function itself
// cannot write, because both writing acts require a named human it is not.
//
// ⚠️ THE REGIME IS NEVER PROPOSED. `reporting-jpm-market-review` deliberately returns
// `regime.message: null` — a regime label asserted by a parser is precisely the fabrication the
// `market_context_missing` sentinel exists to prevent. So the import control REQUIRES the
// curating human to write it, and refuses to submit without it.

import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BookMarked, CheckCircle2, Download, FolderOpen, ImageOff, Send } from "lucide-react";
import { LoadingState, ErrorState, EmptyState } from "@/components/research-hub";
import type { CycleContext } from "./ReportingCycleLayout";
import {
  editionDigestFromFiles,
  type EditionDigest,
} from "@/lib/investment-reporting/editionManifestHash";

// ----------------------------------------------------------------------------
// Local, permissive row shapes (types.ts has not been regenerated for these
// not-yet-applied 0003d tables — see docs/investment-reporting/0003d_market_context.sql).
// ----------------------------------------------------------------------------
interface ChartReadRow {
  id: string;
  chart_id: string;
  chart_key: string | null;
  read_method: string;
  read_kind: string;
  series_label: string;
  as_of_label: string;
  value: number | null;
  value_basis: string | null;
  unit: string | null;
  confidence: string | null;
  needs_human_verify: boolean;
  verified_by: string | null;
  verified_at: string | null;
  verify_note: string | null;
  created_at: string;
}

/**
 * The `reporting-jpm-market-review` 200 body. Mirrors the CONTRACT block in that function's
 * header (supabase/functions/reporting-jpm-market-review/index.ts). `proposed` maps 1:1 onto
 * yardeni_import_top_findings' arguments: `top_findings` is the `_top_findings` argument and
 * `market_context` is `_params.market_context`. No transform is needed and none is applied —
 * the payload the human reads is byte-for-byte the payload that is submitted, except for the
 * regime the human writes into it.
 */
interface JpmProposal {
  ok: true;
  proposed: {
    period_label: string;
    run_type: "quarterly_full" | "monthly_delta";
    as_of_date: string | null;
    market_context: Record<string, unknown>;
    top_findings: unknown[];
  };
  parsed: {
    mode: string;
    chars: number;
    figures_found: number;
    figures_emitted: number;
    dropped: unknown[];
    figures: { label: string; pct: number; matched_on?: string }[];
  };
  next_steps: string[];
}

interface ChartbookEditionRow {
  id: string;
  edition_label: string;
  edition_type: "quarterly" | "monthly";
  period_label: string | null;
  as_of_date: string | null;
  source: string | null;
  licensed_slice: string | null;
  pdf_sha256: string;
  page_count: number | null;
  signed_off_by: string | null;
  signed_off_at: string | null;
  created_at: string;
}

interface SynthesisRunRow {
  id: string;
  cycle_id: string | null;
  run_type: "quarterly_full" | "monthly_delta";
  period_label: string;
  period_type: string;
  regime_label: string | null;
  status: "draft" | "imported" | "curated" | "published" | "superseded" | "failed";
  human_curated: boolean;
  curated_by: string | null;
  curated_at: string | null;
  notes: string | null;
  created_at: string;
}

/**
 * The message off a rejected RPC / edge-function call, without an `any` cast.
 *
 * Every refusal on this page is a SERVER decision worth reading verbatim — a failed publish is
 * the lineage assertion naming the un-traceable figure, and a failed import is the RPC naming
 * the constraint. So the real message is preferred and the fallback is only for a rejection
 * that carries none.
 */
function refusalMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string" && m.trim()) return m;
  }
  return fallback;
}

// ----------------------------------------------------------------------------
// Data layer — local to this page (only one dedicated hook file was scoped: useMarketContext).
// ----------------------------------------------------------------------------
function usePendingChartReads() {
  return useQuery({
    queryKey: ["yardeni_chart_reads", "pending"],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("yardeni_chart_reads")
        .select("*")
        .eq("needs_human_verify", true)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return ((data as ChartReadRow[]) ?? []).filter(Boolean);
    },
  });
}

function useOpenSynthesisRuns(periodLabel: string | undefined) {
  return useQuery({
    queryKey: ["yardeni_synthesis_runs", "open", periodLabel],
    enabled: !!periodLabel,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("yardeni_synthesis_runs")
        .select("*")
        .eq("period_label", periodLabel)
        .in("status", ["draft", "imported", "curated"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return ((data as SynthesisRunRow[]) ?? []).filter(Boolean);
    },
  });
}

function useChartbookEditions(periodLabel: string | undefined) {
  return useQuery({
    queryKey: ["yardeni_chartbook_editions", periodLabel],
    enabled: !!periodLabel,
    staleTime: 60 * 1000,
    queryFn: async () => {
      // period_label is NULLABLE on the table (the RPC writes `_params->>'period_label'`, which
      // is absent when the caller omits it), so an edition registered without one would be
      // invisible under a strict .eq filter. Both are fetched and the caller labels which is
      // which, rather than the roster silently under-reporting.
      const { data, error } = await supabase
        .from("yardeni_chartbook_editions")
        .select("*")
        .or(`period_label.eq.${periodLabel},period_label.is.null`)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return ((data as unknown as ChartbookEditionRow[]) ?? []).filter(Boolean);
    },
  });
}

function useRegisterEdition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      editionLabel: string;
      editionType: "quarterly" | "monthly";
      pdfSha256: string;
      params: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase.rpc("yardeni_register_chartbook_edition", {
        _edition_label: v.editionLabel,
        _edition_type: v.editionType,
        _pdf_sha256: v.pdfSha256,
        _params: v.params as never,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yardeni_chartbook_editions"] }),
  });
}

/**
 * Ask `reporting-jpm-market-review` for a PROPOSED payload. It writes nothing — the response is
 * a draft for the human to read, correct and submit. A non-2xx comes back through
 * `functions.invoke`'s error channel; the function's own `{ok:false,error,detail}` body comes
 * back as DATA with ok:false, so both are surfaced rather than only the transport failure.
 */
function useJpmDraft() {
  return useMutation({
    mutationFn: async (v: {
      periodLabel: string;
      periodType: string;
      body?: string;
      sourceUrl?: string;
      asOfDate?: string | null;
    }) => {
      const { data, error } = await supabase.functions.invoke("reporting-jpm-market-review", {
        body: {
          period_label: v.periodLabel,
          period_type: v.periodType,
          ...(v.body?.trim() ? { body: v.body } : {}),
          ...(v.sourceUrl?.trim() ? { source_url: v.sourceUrl } : {}),
          ...(v.asOfDate ? { as_of_date: v.asOfDate } : {}),
        },
      });
      if (error) throw error;
      const body = data as { ok?: boolean; error?: string; detail?: string } | null;
      if (!body || body.ok !== true) {
        throw new Error(
          [body?.error ?? "the market-review function refused this request", body?.detail]
            .filter(Boolean).join(" — "),
        );
      }
      return data as JpmProposal;
    },
  });
}

function useImportTopFindings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      periodLabel: string;
      runType: "quarterly_full" | "monthly_delta";
      topFindings: unknown;
      params: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase.rpc("yardeni_import_top_findings", {
        _period_label: v.periodLabel,
        _run_type: v.runType,
        _top_findings: v.topFindings as never,
        _params: v.params as never,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yardeni_synthesis_runs"] }),
  });
}

function useVerifyChartRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ readId, value, note }: { readId: string; value?: number | null; note?: string | null }) => {
      const { data, error } = await (supabase as any).rpc("yardeni_verify_chart_read", {
        _read_id: readId,
        _value: value ?? null,
        _note: note ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yardeni_chart_reads"] }),
  });
}

function usePublishMarketContext() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ runId, note }: { runId: string; note?: string | null }) => {
      const { data, error } = await (supabase as any).rpc("yardeni_curate_and_publish", {
        _run_id: runId,
        _note: note ?? null,
      });
      if (error) throw error;
      return data as Record<string, unknown>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["yardeni_synthesis_runs"] });
      qc.invalidateQueries({ queryKey: ["get_current_market_context"] });
    },
  });
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------
export default function YardeniCurate() {
  // The yardeni verify/publish RPCs are still Investment OR Operations only (the CIO ruling of
  // 2026-08-17 / migration 0003r widened the reporting GATE predicate and nothing else), so gate
  // on the team the server reads, not on role_level.
  const { cycle, isInvestmentOrOps } = useOutletContext<CycleContext>();
  const pending = usePendingChartReads();
  const runs = useOpenSynthesisRuns(cycle.period_label);
  const editions = useChartbookEditions(cycle.period_label);
  const verify = useVerifyChartRead();
  const publish = usePublishMarketContext();

  // get_current_market_context maps period_type -> run_type; the same mapping is applied by
  // reporting-jpm-market-review. Derived here rather than typed by the human so the imported
  // run cannot disagree with the cycle it belongs to.
  const runType: "quarterly_full" | "monthly_delta" =
    cycle.period_type === "quarterly-full" ? "quarterly_full" : "monthly_delta";

  return (
    <div className="space-y-6">
      {!isInvestmentOrOps && (
        <Card className="p-4">
          <p className="font-body text-sm text-muted-foreground">
            This surface is read-only for you. Only Investment or Operations may verify a chart read
            or publish the market_context slot, and both signs require a named human (no
            service_role escape). Signing the reporting gate is a separate rule, and admins may do
            that.
          </p>
        </Card>
      )}

      {/* -------------------------------------------------------------------- */}
      {/* 1. Register the chartbook edition                                    */}
      {/* -------------------------------------------------------------------- */}
      <RegisterEditionSection
        periodLabel={cycle.period_label}
        runType={runType}
        disabled={!isInvestmentOrOps}
        editions={editions}
      />

      {/* -------------------------------------------------------------------- */}
      {/* 2. Import the synthesis (JPM-assisted)                               */}
      {/* -------------------------------------------------------------------- */}
      <ImportFindingsSection
        periodLabel={cycle.period_label}
        periodType={cycle.period_type}
        runType={runType}
        disabled={!isInvestmentOrOps}
        editions={editions.data ?? []}
      />

      {/* -------------------------------------------------------------------- */}
      {/* 3. Candidate chart reads awaiting human verify                       */}
      {/* -------------------------------------------------------------------- */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">
          3. Candidate chart reads {pending.data ? `(${pending.data.length} pending)` : ""}
        </h3>
        <p className="font-body text-xs text-muted-foreground">
          Licensed Yardeni/FactSet forward-earnings chart reads. Each candidate needs a named human verify before it
          can back a <code>vendor_chart_verified</code> market figure — clearing this IS the sign.
        </p>

        {pending.isLoading ? (
          <LoadingState rows={2} />
        ) : pending.error ? (
          <ErrorState onRetry={() => pending.refetch()} />
        ) : (pending.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon={<ImageOff className="h-5 w-5" aria-hidden />}
            title="No candidate chart reads pending verify."
          />
        ) : (
          <div className="space-y-2">
            {pending.data!.map((read) => (
              <ChartReadRowCard
                key={read.id}
                read={read}
                disabled={!isInvestmentOrOps}
                onVerify={(value, note) => verify.mutate({ readId: read.id, value, note })}
                pending={verify.isPending && verify.variables?.readId === read.id}
              />
            ))}
          </div>
        )}
        {verify.isError && (
          <p className="font-body text-xs text-destructive">
            {refusalMessage(verify.error, "The verify RPC refused this chart read.")}
          </p>
        )}
      </section>

      {/* -------------------------------------------------------------------- */}
      {/* 4. Open synthesis runs for this period — publish (the second sign)   */}
      {/* -------------------------------------------------------------------- */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">
          4. Synthesis runs — {cycle.period_label}
        </h3>
        <p className="font-body text-xs text-muted-foreground">
          Publishing re-runs the HARD lineage assertion over the whole slot and refuses on any un-traceable or
          fabricated figure — a disabled/failed publish here is the server protecting the reader, not a bug.
        </p>

        {runs.isLoading ? (
          <LoadingState rows={2} />
        ) : runs.error ? (
          <ErrorState onRetry={() => runs.refetch()} />
        ) : (runs.data?.length ?? 0) === 0 ? (
          <EmptyState
            title="No draft/imported/curated synthesis run for this period."
            body="Use step 2 above to import one. Publishing needs an imported run that carries a market_context — yardeni_curate_and_publish refuses a run whose slot is NULL with 'run … has no market_context slot to publish — import it first'."
          />
        ) : (
          <div className="space-y-2">
            {runs.data!.map((run) => (
              <SynthesisRunCard
                key={run.id}
                run={run}
                disabled={!isInvestmentOrOps}
                onPublish={(note) => publish.mutate({ runId: run.id, note })}
                pending={publish.isPending && publish.variables?.runId === run.id}
              />
            ))}
          </div>
        )}
        {publish.isError && (
          <p className="font-body text-xs text-destructive">
            {refusalMessage(publish.error, "The publish RPC refused this run (lineage assertion failed).")}
          </p>
        )}
        {publish.isSuccess && (
          <p className="font-body text-xs text-primary inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Published — the Second Opinion tab now serves this
            slot via get_current_market_context.
          </p>
        )}
      </section>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Step 1 — register the licensed chartbook edition.
//
// `yardeni_register_chartbook_edition(_edition_label, _edition_type, _pdf_sha256, _params)`.
// Verified against live prosrc: _edition_type must be 'quarterly' | 'monthly' (else
// check_violation) and _pdf_sha256 must be non-empty (else not_null_violation, message
// "pdf_sha256 is required (the frozen golden PDF hash)"). It is IDEMPOTENT on
// (edition_label, pdf_sha256) — ON CONFLICT DO NOTHING, then it selects and returns the
// existing id — so re-submitting the same edition returns the same uuid rather than erroring.
// _params is read for: period_label, as_of_date, source (defaults 'yardeni'), licensed_slice,
// storage_path, page_count. The signer is auth.uid(), stamped into signed_off_by/signed_off_at.
// ----------------------------------------------------------------------------
function RegisterEditionSection({
  periodLabel, runType, disabled, editions,
}: {
  periodLabel: string;
  runType: "quarterly_full" | "monthly_delta";
  disabled: boolean;
  editions: ReturnType<typeof useChartbookEditions>;
}) {
  const register = useRegisterEdition();
  const [label, setLabel] = useState("");
  const [pdfSha, setPdfSha] = useState("");
  const [licensedSlice, setLicensedSlice] = useState("");
  const [storagePath, setStoragePath] = useState("");
  const [asOfDate, setAsOfDate] = useState("");
  const [pageCount, setPageCount] = useState("");
  // Set by the folder hasher; null whenever the hash was typed by hand, so the panel never
  // claims "N images hashed" about a digest it did not compute.
  const [digestInfo, setDigestInfo] = useState<EditionDigest | null>(null);
  const [hashing, setHashing] = useState(false);
  const [hashErr, setHashErr] = useState<string | null>(null);
  const [source, setSource] = useState("yardeni");

  // The edition cadence follows the cycle, exactly as run_type does. A monthly cycle attesting
  // a "quarterly" edition would be a lineage error nothing downstream could detect.
  const editionType: "quarterly" | "monthly" = runType === "quarterly_full" ? "quarterly" : "monthly";

  // A sha256 is 64 hex characters. Checked here so an obviously-wrong hash is caught before it
  // is attested rather than after — the RPC only rejects the EMPTY string, so a truncated paste
  // would otherwise land as a valid-looking attestation of a document nobody can re-identify.
  const shaTrimmed = pdfSha.trim().toLowerCase();
  const shaWellFormed = /^[0-9a-f]{64}$/.test(shaTrimmed);
  const canSubmit = !disabled && label.trim().length > 0 && shaWellFormed && !register.isPending;

  return (
    <section className="space-y-2">
      <h3 className="font-heading text-base font-medium text-foreground">
        1. Chartbook edition {editions.data ? `(${editions.data.length} registered)` : ""}
      </h3>
      <p className="font-body text-xs text-muted-foreground">
        Attest the licensed edition these chart reads come from, by its frozen PDF hash. This is
        the lineage root: a <code>vendor_chart_verified</code> figure traces to a verified read,
        and a read traces to an edition. Re-registering the same label and hash is a no-op and
        returns the existing edition.
      </p>

      <Card className="p-4 space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="font-body text-[11px] text-muted-foreground">
              Edition label <span className="text-destructive">*</span>
            </label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={`e.g. Yardeni Chartbook ${periodLabel}`}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="font-body text-[11px] text-muted-foreground">Edition type</label>
            <div className="h-8 flex items-center font-body text-xs text-foreground">
              <code>{editionType}</code>
              <span className="ml-2 text-muted-foreground">
                (follows the cycle&apos;s period_type — not editable)
              </span>
            </div>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label className="font-body text-[11px] text-muted-foreground">
              Edition content hash (sha256) <span className="text-destructive">*</span>
            </label>
            {/* The column is named `pdf_sha256`, but the CBAM chartbook is dozens of PNG/JPG
                pages, not one PDF, and nothing downstream reads this as a PDF — it is the
                idempotency key for (edition_label, hash) and a display string. So take the
                digest over the IMAGE SET. Hashing happens in the browser: the bytes are read
                locally and never uploaded, which is right for a licensed third-party
                chartbook — registering attests that these exact bytes are the edition, it does
                not ask us to store them. */}
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="edition-files"
                type="file"
                multiple
                // @ts-expect-error non-standard but universally supported; lets the operator
                // pick the FOLDER rather than select forty files by hand.
                webkitdirectory=""
                directory=""
                className="hidden"
                onChange={async (e) => {
                  const picked = e.target.files;
                  if (!picked || picked.length === 0) return;
                  setHashing(true);
                  setHashErr(null);
                  try {
                    const out = await editionDigestFromFiles(picked);
                    if (out.pageCount === 0) {
                      setHashErr("No PNG/JPG/WEBP files in that folder — nothing to hash.");
                      setDigestInfo(null);
                    } else {
                      setPdfSha(out.digest);
                      setPageCount(String(out.pageCount));
                      setDigestInfo(out);
                    }
                  } catch (err) {
                    setHashErr(err instanceof Error ? err.message : String(err));
                  } finally {
                    setHashing(false);
                    e.target.value = "";
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={hashing}
                onClick={() => document.getElementById("edition-files")?.click()}
              >
                <FolderOpen className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                {hashing ? "Hashing…" : "Hash the chartbook folder"}
              </Button>
              <span className="font-body text-[11px] text-muted-foreground">
                nothing is uploaded — the digest is computed in your browser
              </span>
            </div>
            {/* The same digest, reproducible outside the browser. Pinned against this exact
                pipeline by tests/investment-reporting/editionManifestHash.test.ts, which runs
                the shell version and asserts it matches — because if the two routes ever
                disagreed, one chartbook would register as two attested editions and the
                lineage assert would have two candidate parents for one slot. */}
            <details className="font-body text-[11px] text-muted-foreground">
              <summary className="cursor-pointer">Compute it outside the browser instead</summary>
              <p className="mt-1">
                Sorted by relative path, one <span className="font-mono">sha256␣␣path</span> line
                per image, then hashed. Run inside the chartbook folder.
              </p>
              <pre className="mt-1 overflow-x-auto rounded bg-muted p-2 font-mono text-[10px] leading-relaxed">
{`find . -type f \\( -iname '*.png' -o -iname '*.jpg' -o -iname '*.jpeg' -o -iname '*.webp' \\) -printf '%P\\n' \\
  | LC_ALL=C sort \\
  | while IFS= read -r f; do printf '%s  %s\\n' "$(sha256sum "$f" | cut -d' ' -f1)" "$f"; done \\
  | sha256sum | cut -d' ' -f1`}
              </pre>
              <p className="mt-1">
                Then set <span className="font-mono">page_count</span> to the number of images.
              </p>
              {/* NO POWERSHELL EQUIVALENT IS OFFERED, DELIBERATELY. The obvious translation uses
                  `Sort-Object -CaseSensitive`, which is still CULTURE-AWARE rather than ordinal,
                  so it can order the same filenames differently from `LC_ALL=C sort` and produce
                  a different digest for the same chartbook — the exact drift the test above
                  exists to prevent, reintroduced in the one place no test can reach. Until
                  someone runs a PowerShell version against the pinned fixture and proves it
                  agrees, the button above is the supported Windows route. */}
              <p className="mt-1">
                On Windows use the button above rather than a PowerShell translation: the obvious
                one sorts by culture rather than by code unit, which can produce a different
                digest for the same folder.
              </p>
            </details>
            <Input
              value={pdfSha}
              onChange={(e) => { setPdfSha(e.target.value); setDigestInfo(null); }}
              placeholder="64 hex characters — pick the folder above, or paste a digest"
              className="h-8 text-xs font-mono"
            />
            {hashErr && (
              <p className="font-body text-[11px] text-destructive">{hashErr}</p>
            )}
            {digestInfo && (
              <p className="font-body text-[11px] text-muted-foreground">
                {digestInfo.pageCount} image{digestInfo.pageCount === 1 ? "" : "s"} hashed;
                {" "}<code>page_count</code> set to {digestInfo.pageCount}.
                {digestInfo.skipped.length > 0 && (
                  <> Skipped {digestInfo.skipped.length} non-image file
                    {digestInfo.skipped.length === 1 ? "" : "s"}:{" "}
                    <span className="font-mono">{digestInfo.skipped.slice(0, 4).join(", ")}</span>
                    {digestInfo.skipped.length > 4 ? " …" : ""}.</>
                )}{" "}
                The digest covers each file&rsquo;s bytes and its path, so a changed, added,
                removed or renamed page changes it.
              </p>
            )}
            {pdfSha.trim().length > 0 && !shaWellFormed && (
              <p className="font-body text-[11px] text-destructive">
                Not a sha256: expected 64 hex characters, got {shaTrimmed.length}. The RPC only
                refuses an EMPTY hash, so a truncated paste would be attested as-is.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <label className="font-body text-[11px] text-muted-foreground">Licensed slice</label>
            <Input
              value={licensedSlice}
              onChange={(e) => setLicensedSlice(e.target.value)}
              placeholder="e.g. I/B/E/S forward-earnings + MSCI intl"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="font-body text-[11px] text-muted-foreground">Source</label>
            <Input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="yardeni"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="font-body text-[11px] text-muted-foreground">As-of date</label>
            <Input
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="font-body text-[11px] text-muted-foreground">Page count</label>
            <Input
              value={pageCount}
              onChange={(e) => setPageCount(e.target.value)}
              placeholder="optional"
              className="h-8 text-xs"
              inputMode="numeric"
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label className="font-body text-[11px] text-muted-foreground">Storage path</label>
            <Input
              value={storagePath}
              onChange={(e) => setStoragePath(e.target.value)}
              placeholder="optional — where the frozen PDF lives"
              className="h-8 text-xs"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() =>
              register.mutate({
                editionLabel: label.trim(),
                editionType,
                pdfSha256: shaTrimmed,
                params: {
                  period_label: periodLabel,
                  ...(asOfDate.trim() ? { as_of_date: asOfDate.trim() } : {}),
                  ...(source.trim() ? { source: source.trim() } : {}),
                  ...(licensedSlice.trim() ? { licensed_slice: licensedSlice.trim() } : {}),
                  ...(storagePath.trim() ? { storage_path: storagePath.trim() } : {}),
                  ...(pageCount.trim() ? { page_count: pageCount.trim() } : {}),
                },
              })
            }
          >
            <BookMarked className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            {register.isPending ? "Registering…" : "Register edition"}
          </Button>
          {register.isError && (
            <p className="font-body text-xs text-destructive">
              {refusalMessage(register.error, "The register RPC refused this edition.")}
            </p>
          )}
          {register.isSuccess && (
            <p className="font-body text-xs text-primary inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Registered — edition{" "}
              <code>{String(register.data).slice(0, 8)}…</code> is available to step 2.
            </p>
          )}
        </div>
      </Card>

      {editions.isLoading ? (
        <LoadingState rows={1} />
      ) : editions.error ? (
        <ErrorState onRetry={() => editions.refetch()} />
      ) : (editions.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<BookMarked className="h-5 w-5" aria-hidden />}
          title="No chartbook edition registered yet."
          body="Nothing downstream is blocked by this on a scorecard-only slot, but a vendor_chart_verified figure cannot exist without one."
        />
      ) : (
        <div className="space-y-2">
          {editions.data!.map((e) => (
            <Card key={e.id} className="p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-body text-sm font-medium text-foreground">{e.edition_label}</div>
                  <div className="font-body text-[11px] text-muted-foreground">
                    {e.edition_type} · {e.period_label ?? "no period_label"}
                    {e.as_of_date ? ` · as of ${e.as_of_date}` : ""}
                    {e.source ? ` · ${e.source}` : ""}
                    {e.licensed_slice ? ` · ${e.licensed_slice}` : ""}
                  </div>
                </div>
                <code className="font-body text-[10px] text-muted-foreground">
                  {e.pdf_sha256.slice(0, 12)}…
                </code>
              </div>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

// ----------------------------------------------------------------------------
// Step 2 — import the synthesis, drafted from the JPM Monthly Market Review.
//
// `yardeni_import_top_findings(_period_label, _run_type, _top_findings, _params)`. Verified
// against live prosrc: _run_type must be 'quarterly_full' | 'monthly_delta'; _params is read
// for period_type, regime_label, market_context, parent_run_id, source_edition_id, as_of_date;
// and it UPSERTS — an existing run for (period_label, run_type) in status draft/imported/curated
// is UPDATED, otherwise a row is inserted at status 'imported'. Neither an edition nor a parent
// run is required.
//
// ⚠️ market_context IS EFFECTIVELY REQUIRED even though the RPC accepts its absence. It is
// written only from `_params->'market_context'`; omit it and the run carries NULL, and step 4
// then refuses with 'run … has no market_context slot to publish — import it first'. So this
// control never submits without one.
// ----------------------------------------------------------------------------
function ImportFindingsSection({
  periodLabel, periodType, runType, disabled, editions,
}: {
  periodLabel: string;
  periodType: string;
  runType: "quarterly_full" | "monthly_delta";
  disabled: boolean;
  editions: ChartbookEditionRow[];
}) {
  const jpm = useJpmDraft();
  const importRun = useImportTopFindings();

  const [pasted, setPasted] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [asOfDate, setAsOfDate] = useState("");
  const [regimeMessage, setRegimeMessage] = useState("");
  const [regimeLabel, setRegimeLabel] = useState("");
  const [editionId, setEditionId] = useState("");
  const [parentRunId, setParentRunId] = useState("");

  const proposal = jpm.data?.proposed;

  // The slot that will actually be submitted: the function's proposal with the human's regime
  // written into it. Never the other way round — nothing here invents a regime, and the payload
  // is otherwise passed through untouched so what the human read is what is stored.
  const slotToSubmit = useMemo(() => {
    if (!proposal) return null;
    const mc = { ...(proposal.market_context ?? {}) };
    mc.regime = {
      ...((mc.regime ?? {}) as Record<string, unknown>),
      message: regimeMessage.trim(),
      source: "CBAM Investment team",
    };
    return mc;
  }, [proposal, regimeMessage]);

  const canDraft = !disabled && !jpm.isPending;
  const canImport =
    !disabled && !!proposal && !!slotToSubmit && regimeMessage.trim().length > 0 && !importRun.isPending;

  return (
    <section className="space-y-2">
      <h3 className="font-heading text-base font-medium text-foreground">
        2. Import the synthesis — {periodLabel} · <code>{runType}</code>
      </h3>
      <p className="font-body text-xs text-muted-foreground">
        Paste the J.P. Morgan AM Monthly Market Review below and draft a proposed slot from it.
        The function is LLM-free and writes nothing: it extracts FACTS (index returns, what led
        and lagged) and hands you a payload to check. JPM&apos;s prose is never republished — it is
        retained only in the private <code>_jpm_sense_check</code> field that arms the
        <code> jpm_overlap</code> guard. You then write the regime, which nothing else may write.
      </p>

      <Card className="p-4 space-y-3">
        <div className="space-y-1">
          <label className="font-body text-[11px] text-muted-foreground">
            Review text (paste-first; leave empty to let the function try the URL)
          </label>
          <Textarea
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder="Paste the monthly market review text or HTML here…"
            className="text-xs min-h-[120px]"
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="font-body text-[11px] text-muted-foreground">Source URL (optional)</label>
            <Input
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="defaults to the JPM AM monthly market review page"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="font-body text-[11px] text-muted-foreground">As-of date (optional)</label>
            <Input
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="h-8 text-xs"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={!canDraft}
            onClick={() =>
              jpm.mutate({
                periodLabel,
                periodType,
                body: pasted,
                sourceUrl,
                asOfDate: asOfDate.trim() || null,
              })
            }
          >
            <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            {jpm.isPending ? "Drafting…" : "Draft from JPM review"}
          </Button>
          {jpm.isError && (
            <p className="font-body text-xs text-destructive">
              {refusalMessage(jpm.error, "The market-review function refused this request.")}
            </p>
          )}
        </div>
      </Card>

      {proposal && (
        <Card className="p-4 space-y-3">
          <div className="font-body text-xs text-muted-foreground">
            Parsed {jpm.data!.parsed.chars.toLocaleString()} characters ({jpm.data!.parsed.mode}) ·{" "}
            {jpm.data!.parsed.figures_emitted} of {jpm.data!.parsed.figures_found} figures emitted
            {jpm.data!.parsed.dropped.length > 0
              ? ` · ${jpm.data!.parsed.dropped.length} dropped as ambiguous`
              : ""}
            .
          </div>

          {jpm.data!.parsed.figures.length > 0 ? (
            <div className="space-y-1">
              <div className="font-body text-[11px] uppercase tracking-wide text-muted-foreground">
                Proposed figures — check every one against the source before importing
              </div>
              {jpm.data!.parsed.figures.map((f, i) => (
                <div key={i} className="flex items-center justify-between gap-3 font-body text-xs">
                  <span className="text-foreground">{f.label}</span>
                  <span className="tabular-nums text-foreground">
                    {f.pct > 0 ? "+" : ""}{f.pct}%
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="font-body text-xs text-muted-foreground">
              No figure parsed unambiguously, so this is a SCORECARD-ONLY slot. That is a valid,
              publishable shape — the lineage assertion returns early when there is no
              <code> market_figures</code> array — not an error.
            </p>
          )}

          {jpm.data!.parsed.dropped.length > 0 && (
            <details className="font-body text-[11px] text-muted-foreground">
              <summary className="cursor-pointer">
                {jpm.data!.parsed.dropped.length} candidate(s) dropped rather than guessed
              </summary>
              <pre className="mt-1 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(jpm.data!.parsed.dropped, null, 2)}
              </pre>
            </details>
          )}

          <div className="space-y-1">
            <label className="font-body text-[11px] text-muted-foreground">
              Regime message <span className="text-destructive">*</span> — your words, not the
              function&apos;s
            </label>
            <Textarea
              value={regimeMessage}
              onChange={(e) => setRegimeMessage(e.target.value)}
              placeholder="e.g. negative-real-funds reflation, long-end-disciplined"
              className="text-xs min-h-[60px]"
            />
            {regimeMessage.trim().length === 0 && (
              <p className="font-body text-[11px] text-muted-foreground">
                The market-review function returns <code>regime.message: null</code> on purpose. A
                regime asserted by a parser is the fabrication the whole slot exists to prevent, so
                the import stays disabled until you write it.
              </p>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="font-body text-[11px] text-muted-foreground">
                Regime label (short, stored on the run)
              </label>
              <Input
                value={regimeLabel}
                onChange={(e) => setRegimeLabel(e.target.value)}
                placeholder="optional"
                className="h-8 text-xs"
              />
            </div>
            <div className="space-y-1">
              <label className="font-body text-[11px] text-muted-foreground">
                Source edition (optional)
              </label>
              <select
                value={editionId}
                onChange={(e) => setEditionId(e.target.value)}
                className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
              >
                <option value="">— none —</option>
                {editions.map((e) => (
                  <option key={e.id} value={e.id}>{e.edition_label}</option>
                ))}
              </select>
            </div>
            {runType === "monthly_delta" && (
              <div className="space-y-1 sm:col-span-2">
                <label className="font-body text-[11px] text-muted-foreground">
                  Parent run id — the published quarterly_full run this month deltas from
                </label>
                <Input
                  value={parentRunId}
                  onChange={(e) => setParentRunId(e.target.value)}
                  placeholder="optional uuid — leave empty to publish with parent_status: missing"
                  className="h-8 text-xs font-mono"
                />
                <p className="font-body text-[11px] text-muted-foreground">
                  Neither import nor publish requires this (verified against both RPCs&apos;
                  prosrc). Leaving it empty is a real choice, not an oversight:
                  <code> get_current_market_context</code> will report{" "}
                  <code>parent_status: &quot;missing&quot;</code> for the monthly slot, which is
                  visible to whoever reads it. Supply it only if a quarterly run has actually been
                  published.
                </p>
              </div>
            )}
          </div>

          <details className="font-body text-[11px] text-muted-foreground">
            <summary className="cursor-pointer">
              Inspect the exact payload that will be submitted
            </summary>
            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap max-h-80">
              {JSON.stringify(
                { _top_findings: proposal.top_findings, _params: { market_context: slotToSubmit } },
                null, 2,
              )}
            </pre>
          </details>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={!canImport}
              onClick={() =>
                importRun.mutate({
                  periodLabel,
                  runType,
                  topFindings: proposal.top_findings,
                  params: {
                    period_type: periodType,
                    market_context: slotToSubmit,
                    ...(regimeLabel.trim() ? { regime_label: regimeLabel.trim() } : {}),
                    ...(editionId ? { source_edition_id: editionId } : {}),
                    ...(parentRunId.trim() ? { parent_run_id: parentRunId.trim() } : {}),
                    ...(proposal.as_of_date ? { as_of_date: proposal.as_of_date } : {}),
                    // NOTE deliberately absent. yardeni_import_top_findings reads EXACTLY six
                    // _params keys (period_type, regime_label, market_context, parent_run_id,
                    // source_edition_id, as_of_date) and ignores everything else silently, so a
                    // note field here would look recorded and be discarded. The run's `notes`
                    // column is written at PUBLISH time from yardeni_curate_and_publish's
                    // `_note`, which step 4 below already offers.
                  },
                })
              }
            >
              <Send className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              {importRun.isPending ? "Importing…" : "Import synthesis"}
            </Button>
            {importRun.isError && (
              <p className="font-body text-xs text-destructive">
                {refusalMessage(importRun.error, "The import RPC refused this payload.")}
              </p>
            )}
            {importRun.isSuccess && (
              <p className="font-body text-xs text-primary inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Imported — the run is now in
                step 4 below, ready to publish.
              </p>
            )}
          </div>
        </Card>
      )}
    </section>
  );
}

// ----------------------------------------------------------------------------
// Row: one candidate chart read + its verify control
// ----------------------------------------------------------------------------
function ChartReadRowCard({
  read,
  disabled,
  pending,
  onVerify,
}: {
  read: ChartReadRow;
  disabled: boolean;
  pending: boolean;
  onVerify: (value: number | null | undefined, note: string | null) => void;
}) {
  const [value, setValue] = useState<string>(read.value != null ? String(read.value) : "");
  const [note, setNote] = useState("");

  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-body text-sm font-medium text-foreground">{read.series_label}</div>
          <div className="font-body text-[11px] text-muted-foreground">
            {read.read_kind} · as of {read.as_of_label} · {read.read_method}
            {read.confidence ? ` · ${read.confidence} confidence` : ""}
          </div>
        </div>
        <div className="font-body text-sm tabular-nums text-foreground">
          {read.value != null ? `${read.value}${read.unit ?? ""}` : "no candidate value"}
          {read.value_basis && <span className="text-muted-foreground text-xs"> ({read.value_basis})</span>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Correct value (optional)"
          className="w-48 h-8 text-xs"
          inputMode="decimal"
        />
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Verify note (optional)"
          className="flex-1 min-w-[160px] h-8 text-xs"
        />
        <Button
          size="sm"
          disabled={disabled || pending}
          onClick={() => {
            const parsed = value.trim() === "" ? undefined : Number(value);
            onVerify(Number.isNaN(parsed as number) ? undefined : parsed, note || null);
          }}
        >
          {pending ? "Verifying…" : "Verify"}
        </Button>
      </div>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Row: one open synthesis run + its publish control
// ----------------------------------------------------------------------------
function SynthesisRunCard({
  run,
  disabled,
  pending,
  onPublish,
}: {
  run: SynthesisRunRow;
  disabled: boolean;
  pending: boolean;
  onPublish: (note: string | null) => void;
}) {
  const [note, setNote] = useState("");
  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-body text-sm font-medium text-foreground">
            {run.regime_label ?? "(no regime label yet)"}
          </div>
          <div className="font-body text-[11px] text-muted-foreground">
            {run.run_type} · status {run.status} · {run.period_type}
          </div>
        </div>
        <span className="font-body text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-muted text-muted-foreground">
          {run.status}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Publish note (optional, recorded on the run)"
          className="flex-1 min-w-[200px] h-8 text-xs"
        />
        <Button size="sm" disabled={disabled || pending} onClick={() => onPublish(note || null)}>
          <Send className="h-3.5 w-3.5 mr-1.5" aria-hidden />
          {pending ? "Publishing…" : "Curate & publish"}
        </Button>
      </div>
    </Card>
  );
}
